// RAG (red/amber/green) scale for Fleet Reports (2026-07-14).
//
// There is NO reusable severity palette in the theme to inherit: app/index.js
// defines primary/secondary/error/major/warning/info but has **no green**, and
// `theme.palette.{error,major,warning,info}` is read ZERO times anywhere in src/
// — severity colour in this app is inline hex, and it has already drifted
// (#fe9f1b vs "#ffa11e " with a trailing space). So we pin the app's de-facto
// hexes here, once, and use them everywhere on this page.
//
// Raw hex is mandatory: both consumers need it — amCharts (am5.color(...)) and
// inline `backgroundColor` on table cells. MUI string colour props serve neither.
//
// DO NOT add `success` to the global MUI theme: 16+ existing `color="success"`
// call sites currently render MUI's #2e7d32 and would all silently repaint.
//
// RAG IS NEVER TINTED BY TENANT BRANDING. Branding is chrome (the StatTile value
// panel uses branding.secondaryColor). RAG is a fixed semantic scale — a tenant
// whose brand colour is red must not make a "good" tile read as bad.
export const RAG = {
  bad: "#bf3535", // app-wide critical/error hex (app/index.js:369, replicated in ~15 files)
  warn: "#fe9f1b", // theme warning.main (app/index.js:376) — standardize on this, not #ffa11e
  good: "#4caf50", // the app's de-facto GOOD/AVAILABLE green (Asset Views, Map, Admin) — NOT MUI #2e7d32
  neutral: "#bfbec8", // no data
};

// Map a value to a RAG colour by its position within [min..max] of its peers.
// reverse=true for higher-is-better metrics (Driving Score).
export const ragFor = (value, min, max, reverse = false) => {
  if (value === null || value === undefined || Number.isNaN(value)) return RAG.neutral;
  if (max === min) return RAG.warn; // all peers equal — nothing to rank
  const pct = (value - min) / (max - min); // 0 = lowest, 1 = highest
  const score = reverse ? pct : 1 - pct; // 1 = best, 0 = worst
  if (score >= 0.66) return RAG.good;
  if (score >= 0.33) return RAG.warn;
  return RAG.bad;
};

// Convenience: RAG a whole series of {value} rows against their own spread.
export const ragForSeries = (rows, reverse = false) => {
  const vals = rows.map((r) => r.value).filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  if (vals.length === 0) return () => RAG.neutral;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  return (value) => ragFor(value, min, max, reverse);
};

// A subtle tinted background for RAG table cells (full-strength hex on a big
// cell is unreadable). Alpha-suffixed hex is fine in every browser we support.
export const ragBg = (color) => (color === RAG.neutral ? "transparent" : `${color}22`);
